import React, { useState, useEffect, useRef, useMemo } from 'react';
import { Head } from '@inertiajs/react';
import AppShell, { type AppShellProps } from '@/components/AppShell';
import { createConsumer } from "@rails/actioncable";

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

interface Guest {
  id: string | number;
  name: string;
  role: string;
  organization?: string;
}

interface Seat {
  seatNo: number;
  guest: Guest | null;
}

interface Table {
  id: string;
  label: string;
  notes: string;
  seatCount: number;
  quota: { admin: number; t1: number; t2: number };
  seats: Seat[];
}

interface Props extends Omit<AppShellProps, 'children'> {
  subdomain?: string;
  event: { id: number; name: string };
  seatingData: Table[];
  seatingLog: any[];
  seatingRoleLabels?: Record<string, string>;
  checkedInIds?: number[];
}

const DEFAULT_ROLE_LABELS: Record<string, string> = {
  admin: 'إداري',
  t1: 'فني 1',
  t2: 'فني 2',
};

/* ------------------------------------------------------------------ */
/*  Floor Plan Configuration                                           */
/* ------------------------------------------------------------------ */

const FLOOR_SLOTS = (function () {
  const slots: [number, number][] = [];
  const rowCols = [11, 10, 9, 7, 6, 5, 4, 2, 1, 0];
  const rowColsReversed = [...rowCols].reverse();

  for (let row = 6; row >= 1; row--) {
    const visualRowIndex = 6 - row;
    const cols = visualRowIndex % 2 === 0 ? rowCols : rowColsReversed;
    cols.forEach(col => slots.push([row, col]));
  }

  const topRowCols = [11, 10, 9, 2, 1, 0];
  const topVisualRowIndex = 6;
  const orderedTopCols = topVisualRowIndex % 2 === 0 ? topRowCols : [...topRowCols].reverse();
  orderedTopCols.forEach(col => slots.push([0, col]));

  return slots;
})();

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

export default function SeatsView(props: Props) {
  const { subdomain, event, seatingData: initialData, seatingLog: initialLog, seatingRoleLabels, checkedInIds = [], urls, translations: t, ...shell } = props;
  const ROLE_LABELS = seatingRoleLabels || DEFAULT_ROLE_LABELS;

  const [tables, setTables] = useState<Table[]>(initialData);
  const [activity, setActivity] = useState<any[]>(initialLog);
  const [selTblId, setSelTblId] = useState<string | null>(initialData[0]?.id || null);
  const [selSeatNo, setSelSeatNo] = useState<number>(1);
  const [searchQuery, setSearchQuery] = useState('');
  const [isSuggOpen, setIsSuggOpen] = useState(false);
  const [activeResults, setActiveResults] = useState<any[]>([]);
  const [colorGuideOpen, setColorGuideOpen] = useState(false);

  // WebSocket – receive updates only, never send
  const channelRef = useRef<any>(null);

  useEffect(() => {
    const cableProtocol = window.location.protocol === 'https:' ? 'wss' : 'ws';
    const cableUrl = `${cableProtocol}://${window.location.host}/cable`;
    // @ts-ignore
    const consumer = createConsumer(cableUrl);
    channelRef.current = consumer.subscriptions.create(
      { channel: "SeatingChannel", event_id: event.id, subdomain },
      {
        received: (data: any) => {
          if (data.type === 'UPDATE_SEATING') {
            setTables(data.tables);
            setActivity(data.log);
          }
        },
      }
    );
    return () => { channelRef.current?.unsubscribe(); };
  }, [event.id, subdomain]);

  const selTbl = useMemo(() => tables.find(t => t.id === selTblId), [tables, selTblId]);
  const selSeat = useMemo(() => selTbl?.seats.find(s => s.seatNo === selSeatNo), [selTbl, selSeatNo]);

  const stats = useMemo(() => {
    const totalSeats = tables.reduce((s, t) => s + t.seatCount, 0);
    const occupied = tables.reduce((s, t) => s + t.seats.filter(s => s.guest).length, 0);
    const issues = tables.filter(t => {
      const rc = roleCount(t);
      return rc.admin !== t.quota.admin || rc.t1 !== t.quota.t1 || rc.t2 !== t.quota.t2;
    }).length;
    const full = tables.filter(t => t.seats.filter(s => s.guest).length >= t.seatCount).length;
    return { totalSeats, occupied, issues, full };
  }, [tables]);

  function roleCount(t: Table) {
    const c = { admin: 0, t1: 0, t2: 0 };
    t.seats.forEach(s => {
      if (!s.guest) return;
      if (s.guest.role === 'admin') c.admin++;
      else if (s.guest.role === 't1') c.t1++;
      else if (s.guest.role === 't2') c.t2++;
    });
    return c;
  }

  function getTblStatus(t: Table) {
    if (t.seats.filter(s => s.guest).length < t.seatCount) return 'ok';
    const rc = roleCount(t);
    if (rc.admin !== t.quota.admin || rc.t1 !== t.quota.t1 || rc.t2 !== t.quota.t2) return 'warn';
    return 'full';
  }

  function tblOccupied(t: Table) { return t.seats.filter(s => s.guest).length; }
  function tblQuotaTotal(t: Table) { return t.quota.admin + t.quota.t1 + t.quota.t2; }

  useEffect(() => {
    if (searchQuery.length < 2) {
      setActiveResults([]);
      setIsSuggOpen(false);
      return;
    }
    const res: any[] = [];
    tables.forEach(t => {
      t.seats.forEach(s => {
        if (s.guest?.name.toLowerCase().includes(searchQuery.toLowerCase())) {
          res.push({ tblId: t.id, seatNo: s.seatNo, name: s.guest.name, tblLabel: t.label, role: s.guest.role });
        }
      });
    });
    setActiveResults(res);
    setIsSuggOpen(true);
  }, [searchQuery, tables]);

  const rc = selTbl ? roleCount(selTbl) : { admin: 0, t1: 0, t2: 0 };

  return (
    <AppShell urls={urls} translations={t} wideLayout={true} fullWidth={true} hideNav={true} {...shell}>
      <Head title={`عرض المقاعد — ${event.name}`} />

      <style>{`
        :root {
          --navy: #1a1f5e; --blue: #1e3a8a; --teal-dk: #0e7490; --teal: #06b6d4;
          --mint: #2dd4bf; --bg: #f0f6fa; --surf: #ffffff; --surf2: #f4fbfd;
          --bdr: #c8e6ef; --txt: #0f1f3d; --muted: #4a6a7c; --r: 10px; --rs: 7px;
          --admin-bg: #b9dbfb; --admin-txt: #073764; --admin-bdr: #1f6fbe;
          --t1-bg: #cfe7a8; --t1-txt: #355f0a; --t1-bdr: #5f8f18;
          --t2-bg: #d7d2ff; --t2-txt: #30277a; --t2-bdr: #675ed2;
          --warn-bg: #ffd9b0; --warn-txt: #8a4800; --warn-bdr: #f08a18;
        }
        .nca-seats-body { font-family: 'Cairo', sans-serif; background: var(--bg); color: var(--txt); margin: 0; padding: 0; }
        .app-hdr {
          background: linear-gradient(135deg, var(--navy), var(--blue) 55%, var(--teal-dk));
          border-bottom: 3px solid var(--teal); padding: 0 20px;
          display: flex; align-items: center; justify-content: space-between;
          flex-wrap: wrap; gap: 12px; min-height: 68px; margin-bottom: 16px; border-radius: var(--r);
        }
        .hdr-title { font-size: 1.1rem; font-weight: 900; color: #fff; }
        .hdr-sub { font-size: 0.72rem; color: var(--teal); font-weight: 700; }
        .hdr-badge { display: flex; align-items: center; gap: 6px; padding: 5px 12px; border-radius: 20px; background: rgba(6, 182, 212, .15); border: 1px solid rgba(6, 182, 212, .35); }
        .hdr-badge .dot { width: 7px; height: 7px; border-radius: 50%; background: var(--teal); animation: pulse 2s infinite; }
        @keyframes pulse { 0%, 100% { opacity: 1 } 50% { opacity: .35 } }
        .hdr-badge span { font-size: 0.75rem; color: var(--teal); font-weight: 700; }
        .ro-badge { display: flex; align-items: center; gap: 5px; padding: 4px 10px; border-radius: 20px; background: rgba(255,255,255,.12); border: 1px solid rgba(255,255,255,.25); font-size: 0.72rem; color: rgba(255,255,255,.85); font-weight: 700; }
        .layout { display: grid; grid-template-columns: 1fr min(380px, 100%); gap: 16px; padding: 16px 0; max-width: 100%; align-items: start; direction: rtl; }
        .stat-card { background: var(--surf); border-radius: var(--r); padding: 12px 14px; border: 1px solid var(--bdr); border-top: 3px solid var(--teal); }
        .stat-card h4 { font-size: 0.7rem; font-weight: 700; color: var(--muted); margin-bottom: 4px; }
        .stat-card .val { font-size: 1.5rem; font-weight: 900; background: linear-gradient(135deg, var(--navy), var(--teal-dk)); -webkit-background-clip: text; -webkit-text-fill-color: transparent; }
        .toolbar { background: var(--surf); border-radius: var(--r); padding: 14px 16px; border: 1px solid var(--bdr); box-shadow: 0 1px 4px rgba(14, 116, 144, .08); }
        .floor-plan { display: grid; grid-template-columns: repeat(12, minmax(78px, 1fr)); gap: 10px 8px; background: var(--surf2); border: 1px solid var(--bdr); border-radius: var(--r); padding: 14px; direction: ltr; min-width: 1080px; }
        .tbl-card { position: relative; border-radius: var(--r); border: 2px solid var(--bdr); background: var(--surf); padding: 8px 6px 6px; cursor: pointer; transition: all .15s; display: flex; flex-direction: column; gap: 5px; }
        .tbl-card:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(14, 116, 144, .15); }
        .tbl-card.st-ok { border-color: #4f8ff7; background: linear-gradient(180deg, #fff, #dcebff); }
        .tbl-card.st-warn { border-color: #f08a18; background: linear-gradient(180deg, #fff, #fff0df); }
        .tbl-card.st-full { border-color: #E24B4A; background: linear-gradient(180deg, #fff, #FCEBEB); }
        .tbl-card.selected { box-shadow: 0 0 0 3px rgba(6, 182, 212, .3); }
        .tbl-card.sh-hit { border-color: #f59e0b !important; box-shadow: 0 0 0 4px rgba(245, 158, 11, .2) !important; }
        .tbl-badge { position: absolute; top: -11px; left: 50%; transform: translateX(-50%); font-size: 0.56rem; font-weight: 800; padding: 1px 8px; border-radius: 20px; white-space: nowrap; z-index: 3; background: var(--teal-dk); color: #fff; box-shadow: 0 1px 4px rgba(0,0,0,.18); }
        .st-ok .tbl-badge { background: #2f6fd8; }
        .st-warn .tbl-badge { background: #c56b08; }
        .st-full .tbl-badge { background: #A32D2D; }
        .sm { width: 16px; height: 16px; border-radius: 3px; border: 1px solid transparent; transition: transform .1s; display: flex; align-items: center; justify-content: center; }
        .sm.empty { background: #dcebff; border-color: #4f8ff7; }
        .sm.occ-admin { background: var(--admin-bg); border-color: var(--admin-bdr); }
        .sm.occ-t1 { background: var(--t1-bg); border-color: var(--t1-bdr); }
        .sm.occ-t2 { background: var(--t2-bg); border-color: var(--t2-bdr); }
        .sm.sh { background: #fef3c7 !important; border-color: #f59e0b !important; box-shadow: 0 0 0 2px rgba(245,158,11,.25); }
        .sm.ci { position: relative; }
        .sm.ci::after { content: '✓'; position: absolute; inset: 0; display: flex; align-items: center; justify-content: center; font-size: 11px; font-weight: 900; color: #16a34a; line-height: 1; pointer-events: none; }
        .side-panel { position: sticky; top: 14px; background: var(--surf); border-radius: var(--r); border: 1px solid var(--bdr); box-shadow: 0 8px 32px rgba(14,116,144,.12); overflow: hidden; display: flex; flex-direction: column; max-height: calc(100vh - 30px); }
        .scroll-container { overflow-x: auto; overflow-y: hidden; padding-bottom: 8px; -webkit-overflow-scrolling: touch; direction: ltr; }
        .scroll-container::-webkit-scrollbar { height: 6px; }
        .scroll-container::-webkit-scrollbar-track { background: var(--surf2); border-radius: 10px; }
        .scroll-container::-webkit-scrollbar-thumb { background: var(--bdr); border-radius: 10px; }
        .scroll-container::-webkit-scrollbar-thumb:hover { background: var(--teal); }
        .sp-hdr { padding: 12px 18px; border-bottom: 1px solid var(--bdr); background: linear-gradient(135deg, var(--navy), var(--teal-dk)); display: flex; align-items: center; justify-content: space-between; }
        .sp-hdr h3 { font-size: 0.95rem; font-weight: 800; color: #fff; }
        .psec { padding: 12px 16px; border-bottom: 1px solid var(--bdr); }
        .sp-body { flex: 1; min-height: 0; overflow-y: auto; }
        .sp-body::-webkit-scrollbar { width: 5px; }
        .sp-body::-webkit-scrollbar-thumb { background: var(--bdr); border-radius: 10px; }
        .sec-ttl { font-size: 0.64rem; font-weight: 800; text-transform: uppercase; letter-spacing: .07em; color: var(--teal-dk); margin-bottom: 9px; padding-bottom: 5px; border-bottom: 1.5px solid var(--bdr); }
        .quota-box { border-radius: var(--rs); padding: 8px 5px; text-align: center; border: 1.5px solid transparent; }
        .quota-box .qval { font-size: 1.15rem; font-weight: 900; }
        .quota-box .qlabel { font-size: 0.62rem; font-weight: 700; margin-top: 2px; }
        .quota-box .qdiff { font-size: 0.64rem; font-weight: 800; margin-top: 2px; }
        .qb-admin { background: var(--admin-bg); color: var(--admin-txt); border-color: var(--admin-bdr); }
        .qb-t1 { background: var(--t1-bg); color: var(--t1-txt); border-color: var(--t1-bdr); }
        .qb-t2 { background: var(--t2-bg); color: var(--t2-txt); border-color: var(--t2-bdr); }
        .qb-warn { background: var(--warn-bg); color: var(--warn-txt); border-color: var(--warn-bdr); }
        .qb-muted { background: #eceff3; color: #5b6573; border-color: #c7ced8; }
        @media(max-width:1024px) {
          .layout { grid-template-columns: 1fr; padding: 12px; gap: 12px; }
          .side-panel { position: static; max-height: none; }
          .floor-plan { min-width: 800px; padding: 8px; gap: 6px; }
        }
        @media(max-width:640px) {
          .app-hdr { flex-direction: column; align-items: stretch; text-align: center; gap: 16px; }
        }
      `}</style>

      <div className="nca-seats-body" dir="rtl">

        {/* Header */}
        <header className="app-hdr">
          <div style={{ display: 'flex', alignItems: 'center', gap: '14px' }}>
            <div style={{ background: '#fff', borderRadius: '8px', padding: '5px 12px', border: '2px solid rgba(6,182,212,.3)', boxShadow: '0 0 0 3px rgba(6,182,212,.1)' }}>
              <span style={{ fontSize: '0.9rem', fontWeight: 900, color: '#1a1f5e' }}>NCA</span>
            </div>
            <div style={{ width: '1px', height: '36px', background: 'rgba(6,182,212,.3)' }} />
            <div>
              <div className="hdr-title">{event.name}</div>
              <div className="hdr-sub">عرض القاعة — قراءة فقط</div>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <div className="hdr-badge">
              <div className="dot" /><span>مباشر</span>
            </div>
            <div className="ro-badge">👁 عرض فقط</div>
            <button onClick={() => window.location.reload()} className="text-white/70 hover:text-white px-3 py-1 text-sm font-bold border border-white/20 rounded-md">تحديث</button>
            <a href={urls.events} className="text-white/70 hover:text-white px-2 py-1 text-sm font-bold">الرجوع</a>
          </div>
        </header>

        <div className="layout">
          <div className="flex flex-col gap-4 min-w-0">

            {/* Stats */}
            <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
              <SummaryCard label="الطاولات" value={tables.length} />
              <SummaryCard label="غير مكتملة" value={stats.totalSeats - stats.occupied > 0 ? tables.length - stats.full : 0} />
              <SummaryCard label="خلل توزيع" value={stats.issues} />
              <SummaryCard label="مكتملة" value={stats.full} />
              <SummaryCard label="إجمالي المقاعد" value={stats.totalSeats} />
              <SummaryCard label="مشغولة" value={stats.occupied} />
              <SummaryCard label="متبقية" value={stats.totalSeats - stats.occupied} />
              <SummaryCard label="حضور" value={stats.occupied} />
            </div>

            {/* Search */}
            <div className="toolbar">
              <label style={{ display: 'block', fontSize: '0.72rem', fontWeight: 700, color: 'var(--muted)', marginBottom: '5px' }}>البحث عن شخص</label>
              <div className="relative">
                <input
                  value={searchQuery}
                  onChange={e => setSearchQuery(e.target.value)}
                  placeholder="اكتب اسم الشخص للبحث..."
                  className="w-full p-2 border border-blue-100 rounded-md outline-none focus:ring-2 focus:ring-cyan-500/20"
                />
                {isSuggOpen && activeResults.length > 0 && (
                  <div className="absolute top-full right-0 left-0 z-50 mt-1 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
                    {activeResults.map((res, i) => (
                      <button
                        key={i}
                        onClick={() => { setSelTblId(res.tblId); setSelSeatNo(res.seatNo); setIsSuggOpen(false); setSearchQuery(res.name); }}
                        className="flex w-full flex-col p-3 text-right hover:bg-slate-50 border-b last:border-0"
                      >
                        <span className="text-sm font-bold">{res.name}</span>
                        <span className="text-xs text-slate-500">{res.tblLabel} — مقعد {res.seatNo} · {ROLE_LABELS[res.role]}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Floor Plan */}
            <div className="bg-white rounded-[10px] border border-blue-100 p-4 shadow-sm">
              <div className="flex items-center gap-2 mb-1">
                <h2 className="text-[0.95rem] font-extrabold text-slate-800">مخطط القاعة</h2>
                <span className="px-2 py-0.5 rounded-full bg-slate-50 border border-slate-100 text-[0.68rem] font-bold text-slate-500">{tables.length} طاولة</span>
              </div>
              <p className="text-[0.76rem] text-slate-500 mb-3">انقر على طاولة لعرض التفاصيل. وضع القراءة فقط — لا يمكن تعديل المقاعد.</p>
              <div className="scroll-container">
                <div className="floor-plan">
                  <div className="flex items-center justify-center border-2 border-dashed border-blue-100 rounded-md text-slate-400 text-[0.7rem] font-bold min-h-[62px]" style={{ gridColumn: '5/9', gridRow: '1/2', direction: 'rtl' }}>
                    منطقة التحكم
                  </div>
                  {tables.map((t, i) => {
                    const slot = FLOOR_SLOTS[i];
                    if (!slot) return null;
                    const st = getTblStatus(t);
                    const isSelected = selTblId === t.id;
                    const isHit = isSelected && searchQuery.trim().length >= 2;
                    return (
                      <div
                        key={t.id}
                        onClick={() => setSelTblId(t.id)}
                        style={{ gridRow: `${slot[0] + 1}`, gridColumn: `${slot[1] + 1}` }}
                        className={`tbl-card st-${st} ${isSelected ? 'selected' : ''} ${isHit ? 'sh-hit' : ''}`}
                      >
                        <span className="tbl-badge">{t.label}</span>
                        <div className="flex flex-wrap gap-[3px] justify-center mt-1">
                          {t.seats.map(s => (
                            <div
                              key={s.seatNo}
                              onClick={e => { e.stopPropagation(); setSelTblId(t.id); setSelSeatNo(s.seatNo); }}
                              className={`sm ${!s.guest ? 'empty' : `occ-${s.guest.role}`} ${isSelected && s.seatNo === selSeatNo && searchQuery.trim().length >= 2 ? 'sh' : ''} ${s.guest && checkedInIds.includes(Number(s.guest.id)) ? 'ci' : ''}`}
                              title={s.guest ? `${s.guest.name} (${ROLE_LABELS[s.guest.role]})` : `مقعد ${s.seatNo}`}
                            />
                          ))}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>

          </div>

          {/* Side Panel — read only */}
          <aside className="side-panel">
            <div className="sp-hdr">
              <h3>{selTbl?.label || 'تفاصيل الطاولة'}</h3>
              <div className="hdr-badge" style={{ background: 'rgba(255,255,255,.12)', border: '1px solid rgba(255,255,255,.25)' }}>
                <div className="dot" /><span>مباشر</span>
              </div>
            </div>

            <div className="sp-body">
              {selTbl ? (
                <>
                  {/* Table info */}
                  <div className="psec">
                    <div className="sec-ttl">معلومات الطاولة</div>
                    <div className="text-sm font-bold text-slate-800 mb-3">{selTbl.label}</div>
                    <div className="grid grid-cols-2 gap-y-1 text-xs">
                      <span className="text-slate-400">المقاعد الكلية</span><span className="font-bold">{selTbl.seatCount}</span>
                      <span className="text-slate-400">مشغولة</span><span className="font-bold">{tblOccupied(selTbl)}</span>
                      <span className="text-slate-400">متبقية</span><span className="font-bold">{selTbl.seatCount - tblOccupied(selTbl)}</span>
                    </div>
                    {selTbl.notes ? (
                      <div className="mt-3 p-2 bg-slate-50 border border-blue-50 rounded-md text-xs text-slate-600">{selTbl.notes}</div>
                    ) : null}
                  </div>

                  {/* Quota display */}
                  <div className="psec">
                    <div className="sec-ttl">توزيع الأدوار والحصص</div>
                    <div className="grid grid-cols-3 gap-1.5">
                      <QuotaBox label={ROLE_LABELS.admin} actual={rc.admin} quota={selTbl.quota.admin} cls="qb-admin" full={tblOccupied(selTbl) >= selTbl.seatCount} />
                      <QuotaBox label={ROLE_LABELS.t1} actual={rc.t1} quota={selTbl.quota.t1} cls="qb-t1" full={tblOccupied(selTbl) >= selTbl.seatCount} />
                      <QuotaBox label={ROLE_LABELS.t2} actual={rc.t2} quota={selTbl.quota.t2} cls="qb-t2" full={tblOccupied(selTbl) >= selTbl.seatCount} />
                    </div>
                    <p className="mt-2 text-[0.68rem] text-slate-500">
                      المقاعد غير الموزعة: {selTbl.seatCount - tblQuotaTotal(selTbl)}
                    </p>
                  </div>

                  {/* Selected seat info */}
                  <div className="psec">
                    <div className="sec-ttl">معلومات المقعد المحدد</div>
                    <div className="grid grid-cols-2 gap-y-1 text-xs mb-2">
                      <span className="text-slate-400">الطاولة</span><span className="font-bold">{selTbl.label}</span>
                      <span className="text-slate-400">المقعد</span><span className="font-bold">{selSeatNo}</span>
                    </div>
                    {selSeat?.guest ? (
                      <div className="rounded-md border border-blue-100 bg-slate-50 p-3">
                        <div className="text-sm font-bold text-slate-800">{selSeat.guest.name}</div>
                        <div className="text-xs text-slate-500 mt-1">
                          <span className={`inline-block px-2 py-0.5 rounded-full text-[0.65rem] font-bold ${
                            selSeat.guest.role === 'admin' ? 'bg-[var(--admin-bg)] text-[var(--admin-txt)]' :
                            selSeat.guest.role === 't1'    ? 'bg-[var(--t1-bg)] text-[var(--t1-txt)]' :
                                                             'bg-[var(--t2-bg)] text-[var(--t2-txt)]'
                          }`}>{ROLE_LABELS[selSeat.guest.role]}</span>
                        </div>
                        {selSeat.guest.organization && (
                          <div className="text-xs text-slate-400 mt-1">{selSeat.guest.organization}</div>
                        )}
                      </div>
                    ) : (
                      <div className="rounded-md border border-dashed border-blue-100 bg-slate-50 p-3 text-center text-xs text-slate-400">مقعد فارغ</div>
                    )}

                    {/* Seat selector */}
                    <div className="flex flex-wrap gap-1 mt-3">
                      {selTbl.seats.map(s => (
                        <button
                          key={s.seatNo}
                          onClick={() => setSelSeatNo(s.seatNo)}
                          className={`w-7 h-7 rounded text-[0.62rem] font-bold border transition-all ${
                            s.seatNo === selSeatNo
                              ? 'bg-cyan-600 text-white border-cyan-600'
                              : s.guest
                              ? `border occ-${s.guest.role}`
                              : 'bg-blue-50 border-blue-100 text-slate-600'
                          }`}
                          style={s.guest && s.seatNo !== selSeatNo ? {
                            background: s.guest.role === 'admin' ? 'var(--admin-bg)' : s.guest.role === 't1' ? 'var(--t1-bg)' : 'var(--t2-bg)',
                            borderColor: s.guest.role === 'admin' ? 'var(--admin-bdr)' : s.guest.role === 't1' ? 'var(--t1-bdr)' : 'var(--t2-bdr)',
                            color: s.guest.role === 'admin' ? 'var(--admin-txt)' : s.guest.role === 't1' ? 'var(--t1-txt)' : 'var(--t2-txt)',
                          } : {}}
                          title={s.guest ? `${s.guest.name} (${ROLE_LABELS[s.guest.role]})` : `مقعد ${s.seatNo} فارغ`}
                        >
                          {s.seatNo}
                        </button>
                      ))}
                    </div>
                  </div>

                  {/* Seats list */}
                  <div className="psec">
                    <div className="sec-ttl">قائمة الضيوف في الطاولة</div>
                    <div className="flex flex-col gap-1">
                      {selTbl.seats.filter(s => s.guest).length === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-2">لا يوجد ضيوف في هذه الطاولة</p>
                      ) : (
                        selTbl.seats.filter(s => s.guest).map(s => (
                          <div key={s.seatNo} className="flex items-center gap-2 p-2 rounded-md bg-slate-50 border border-blue-50">
                            <span className="text-[0.65rem] font-bold text-slate-400 w-5 text-center">{s.seatNo}</span>
                            <div className="flex-1 min-w-0">
                              <div className="text-xs font-bold text-slate-800 truncate">{s.guest!.name}</div>
                            </div>
                            <span className={`shrink-0 px-1.5 py-0.5 rounded-full text-[0.6rem] font-bold ${
                              s.guest!.role === 'admin' ? 'bg-[var(--admin-bg)] text-[var(--admin-txt)]' :
                              s.guest!.role === 't1'    ? 'bg-[var(--t1-bg)] text-[var(--t1-txt)]' :
                                                          'bg-[var(--t2-bg)] text-[var(--t2-txt)]'
                            }`}>{ROLE_LABELS[s.guest!.role]}</span>
                          </div>
                        ))
                      )}
                    </div>
                  </div>

                  {/* Color guide */}
                  <div className="psec">
                    <button
                      onClick={() => setColorGuideOpen(!colorGuideOpen)}
                      className="w-full cursor-pointer bg-transparent border-none p-0 text-right"
                      style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: '0.64rem', fontWeight: 800, textTransform: 'uppercase' as const, letterSpacing: '.07em', color: 'var(--teal-dk)', marginBottom: colorGuideOpen ? 9 : 0, paddingBottom: 5, borderBottom: '1.5px solid var(--bdr)' }}
                    >
                      <span>دليل الألوان</span>
                      <span style={{ fontSize: '0.7rem', transform: colorGuideOpen ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform .2s' }}>▼</span>
                    </button>
                    {colorGuideOpen && (
                      <div className="flex flex-col gap-2 mt-1">
                        {[
                          { bg: 'var(--admin-bg)', bdr: 'var(--admin-bdr)', label: ROLE_LABELS.admin },
                          { bg: 'var(--t1-bg)',    bdr: 'var(--t1-bdr)',    label: ROLE_LABELS.t1 },
                          { bg: 'var(--t2-bg)',    bdr: 'var(--t2-bdr)',    label: ROLE_LABELS.t2 },
                        ].map(r => (
                          <div key={r.label} className="flex items-center gap-2.5">
                            <span style={{ width: 16, height: 16, borderRadius: 3, background: r.bg, border: `1.5px solid ${r.bdr}`, flexShrink: 0 }} />
                            <span className="text-xs font-bold text-slate-700">{r.label}</span>
                          </div>
                        ))}
                        <div style={{ borderTop: '1px solid var(--bdr)', margin: '4px 0' }} />
                        {[
                          { bg: 'linear-gradient(180deg,#fff,#dcebff)', bdr: '#4f8ff7', label: 'غير مكتملة' },
                          { bg: 'linear-gradient(180deg,#fff,#fff0df)', bdr: '#f08a18', label: 'خلل في التوزيع' },
                          { bg: 'linear-gradient(180deg,#fff,#FCEBEB)', bdr: '#E24B4A', label: 'مكتملة' },
                        ].map(r => (
                          <div key={r.label} className="flex items-center gap-2.5">
                            <span style={{ width: 16, height: 16, borderRadius: 3, background: r.bg, border: `1.5px solid ${r.bdr}`, flexShrink: 0 }} />
                            <span className="text-xs font-bold text-slate-700">{r.label}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  {/* Activity log */}
                  <div className="psec bg-slate-50/30">
                    <div className="sec-ttl">سجل النشاط</div>
                    <div className="flex flex-col gap-1.5 max-h-[240px] overflow-y-auto pl-1">
                      {activity.length === 0 ? (
                        <p className="text-xs text-slate-400 text-center py-2">لا يوجد نشاط بعد</p>
                      ) : (
                        activity.map((act, i) => (
                          <div key={i} className="bg-white border border-blue-50 p-2 rounded-md">
                            <div className="text-[11px] font-bold text-slate-800">{act.text}</div>
                            <div className="text-[9px] text-slate-400 mt-0.5">{act.ts}</div>
                          </div>
                        ))
                      )}
                    </div>
                  </div>
                </>
              ) : (
                <div className="p-20 text-center text-slate-300">
                  <p>اختر طاولة للبدء</p>
                </div>
              )}
            </div>
          </aside>
        </div>
      </div>
    </AppShell>
  );
}

function SummaryCard({ label, value }: { label: string; value: number }) {
  return (
    <div className="stat-card">
      <h4>{label}</h4>
      <div className="val">{value}</div>
    </div>
  );
}

function QuotaBox({ label, actual, quota, cls, full }: { label: string; actual: number; quota: number; cls: string; full: boolean }) {
  const diff = actual - quota;
  const isOk = diff === 0;
  const statusCls = !full ? 'qb-muted' : (!isOk ? 'qb-warn' : cls);
  const diffStr = diff > 0 ? `+${diff}` : diff < 0 ? `${diff}` : '✓';
  return (
    <div className={`quota-box ${statusCls}`}>
      <div className="qval">{actual}</div>
      <div className="qlabel">{label}</div>
      <div className="qdiff">مطلوب: {quota} <b>{diffStr}</b></div>
    </div>
  );
}
